import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

// third party
import { IntlProvider } from 'react-intl';
import useConfig from 'hooks/useConfig';
import { registerApiRequestTimeoutMessage } from 'api/axiosInstance';

// load locales files
function loadLocaleData(i18n) {
  switch (i18n) {
    case 'fr':
      return import('utils/locales/fr.json');
    case 'de':
      return import('utils/locales/de.json');
    case 'ja':
      return import('utils/locales/ja.json');
    case 'tw':
      return import('utils/locales/tw.json');
    case 'zh':
      return import('utils/locales/zh.json');
    default:
      return import('utils/locales/en.json');
  }
}

export default function Locales({ children }) {
  const { i18n } = useConfig();
  const [messages, setMessages] = useState();

  useEffect(() => {
    // English is loaded as the BASE, with the selected locale layered over it.
    //
    // `defaultLocale` does not do this. It only picks the locale data used for formatting; it never
    // supplies a missing message. Because one catalog is loaded at a time, a key that exists in
    // en.json but not yet in the selected language rendered as the raw id - a user saw
    // "prWriteUnconfirmed" where a sentence belonged. Falling back to English is not a translation,
    // but it is readable, and it is what the untranslated ids need until they are translated.
    let current = true;
    Promise.all([import('utils/locales/en.json'), loadLocaleData(i18n)]).then(([english, selected]) => {
      if (!current) {
        return;
      }
      const next = i18n === 'en' ? selected.default : { ...english.default, ...selected.default };
      registerApiRequestTimeoutMessage(next);
      setMessages(next);
    });
    return () => {
      current = false;
    };
  }, [i18n]);

  return (
    <>
      {messages && (
        <IntlProvider locale={i18n} defaultLocale="en" messages={messages}>
          {children}
        </IntlProvider>
      )}
    </>
  );
}

Locales.propTypes = { children: PropTypes.node };
